// src/App.js
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { listenToAuthChanges } from "./store/authSlice";
import Wrapper from "./shared/components/Wrapper";
import GlobalLoader from "./shared/ui/GlobalLoader";
import ProtectedRoute from "./shared/components/ProtectedRoute";
import { MAIN_TENANT_ID } from "./assets/constants/site.constants";

const GenericTenantPage = lazy(() => import("./pages/GenericTenantPage"));
const ProductDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const TenantRouteResolver = lazy(() => import("./pages/TenantRouteResolver"));

const PortalLayout = lazy(() => import("./features/portal/PortalLayout"));
const PortalHomePage = lazy(() => import("./features/portal/PortalHomePage"));
const Dashboard = lazy(() => import("./features/portal/dashboard/Dashboard"));
const CrmModule = lazy(() => import("./features/portal/crm/CrmModule"));
const RbacPlaceholder = lazy(
  () => import("./features/portal/tenant-management/RbacPlaceholder"),
);
const DocumentsModule = lazy(
  () => import("./features/portal/documents/DocumentsModule"),
);

const CmsPage = lazy(
  () => import("./features/portal/tenant-management/CmsPage"),
);
const SiteSettingsEditor = lazy(
  () => import("./features/portal/tenant-management/SiteSettingsEditor"),
);

const DocumentsPrivateManager = lazy(
  () => import("./features/portal/documents/DocumentsPrivateManager"),
);

const MarketingModule = lazy(
  () => import("./features/portal/marketing/MarketingModule"),
);

const SRMModule = lazy(() => import("./features/portal/srm/SRMModule"));

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const unsubscribe = dispatch(listenToAuthChanges());
    return () => unsubscribe();
  }, [dispatch]);

  return (
    <BrowserRouter>
      <Wrapper>
        <GlobalLoader />
        <Suspense fallback={<GlobalLoader />}>
          <Routes>
            {/* Rutas públicas */}
            <Route
              path="/"
              element={<Navigate to={`/${MAIN_TENANT_ID}`} replace />}
            />
            {/* FIX: Agregado /* para permitir rutas anidadas en TenantRouteResolver */}
            <Route path="/:tenantId/*" element={<TenantRouteResolver />} />

            <Route path="/:tenantId/login" element={<LoginPage />} />
            <Route
              path="/:tenantId/servicios/:serviceId"
              element={<ProductDetailPage />}
            />
            {/* Esta ruta genérica debe ir al final de las públicas específicas */}
            <Route path="/:tenantId/:pageKey" element={<GenericTenantPage />} />

            {/* Rutas Protegidas del Portal */}
            <Route element={<ProtectedRoute />}>
              <Route path="/:tenantId/portal" element={<PortalLayout />}>
                <Route index element={<PortalHomePage />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="crm" element={<CrmModule />} />
                <Route path="rbac" element={<RbacPlaceholder />} />
                <Route path="cms" element={<CmsPage />} />
                <Route path="cms/settings" element={<SiteSettingsEditor />} />
                <Route path="marketing" element={<MarketingModule />} />
                <Route path="srm" element={<SRMModule />} />
                <Route path="documents" element={<DocumentsModule />} />
              </Route>
            </Route>

            {/* Fallback */}
            <Route
              path="*"
              element={<Navigate to={`/${MAIN_TENANT_ID}`} replace />}
            />
          </Routes>
        </Suspense>
      </Wrapper>
    </BrowserRouter>
  );
}

export default App;
